import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CateringPastOrderCard from "./cateringPastOrderCard";
import { getCateringPastOrdersAsync } from "../../redux/features/cateringCustomer/cateringCustomerThunks";
import {
  selectCateringServicePastOrders,
  selectCateringSearchedCustomer,
} from "../../redux/features/cateringCustomer/cateringCustomerSelectors";
import { selectServiceType } from "../../redux/features/customer/customerSelectors";
import { selectSearchedCompany } from "../../redux/features/company/companySelectors";

const CateringPastOrders = () => {
  const dispatch = useDispatch();
  
  // Get data from Redux state
  const cateringPastOrders = useSelector(selectCateringServicePastOrders);
  const customerData = useSelector(selectCateringSearchedCustomer);
  const serviceType = useSelector(selectServiceType);
  const companyData = useSelector(selectSearchedCompany);
  
  const [transformedOrders, setTransformedOrders] = useState([]);

  // Transform catering orders data to match the card component props
  const transformCateringOrders = (orders) => {
    if (!orders || !Array.isArray(orders)) return [];
    
    return orders.map(order => ({
      id: order.order_id,
      restaurant: order.restaurant_name_en || order.restaurant_name_ar || "Unknown Restaurant",
      serviceItemName: order.service_item_name_en || order.service_item_name_ar || "Catering Service",
      date: formatDate(order.service_item_date),
      orderStatus: order.order_status || "Unknown",
      notes: order.special_instructions_1 || order.special_instructions_2 || order.comments || "",
      orderId: order.order_id,
      orderNo: order.order_no || "",
      totalPrice: order.order_total_price?.toString() || order.service_item_price || "0.000",
      currency: order.currency || "KWD",
      // Additional data needed for navigation and cart restoration
      serviceItemId: order.service_item_id,
      restaurantId: order.restaurant_id,
      noOfPax: order.no_of_pax,
      serviceItemDate: order.service_item_date,
      serviceItemSlot: order.service_item_slot,
      serviceItems: order.service_item || [],
      customerName: `${order.customer_first_name || ''} ${order.customer_last_name || ''}`.trim(),
      orderDate: order.order_date,
      // Modification tracking
      updatedByAgentId: order.updated_by_agent_id?.toString() || "",
      updatedDatetime: order.updated_datetime || "",
    }));
  };

  // Format date to a readable format
  const formatDate = (dateString) => {
    if (!dateString) return "";
    
    try {
      // Handle DD-MM-YYYY format from API
      const [day, month, year] = dateString.split('-');
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  // Load catering past orders when customer data is available
  useEffect(() => {
    if (serviceType === "individual catering" && customerData?.id) {
      // For individual catering: customer_id = searched customer id, company_id = 0
      dispatch(getCateringPastOrdersAsync(customerData.id.toString(), "individual", "0"));
    } else if (serviceType === "corporate catering" && companyData?.id) {
      // For corporate catering: customer_id = 0, company_id = searched company id
      dispatch(getCateringPastOrdersAsync("0", "corporate", companyData.id.toString()));
    }
  }, [customerData, serviceType, companyData, dispatch]);

  // Transform orders when catering past orders data changes
  useEffect(() => {
    if (cateringPastOrders) {
      const transformed = transformCateringOrders(cateringPastOrders);
      setTransformedOrders(transformed);
    }
  }, [cateringPastOrders]);

  const hasOrders = transformedOrders && transformedOrders.length > 0;

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-500 mb-4">Catering Past Orders</h1>
      
      {/* Orders list */}
      {hasOrders ? (
        <div className="flex flex-col gap-4">
          <div 
            className="grid gap-4 overflow-y-auto overflow-x-hidden max-h-[550px]" 
            style={{ gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))" }}
          >
            {transformedOrders
              ?.sort((a, b) => {
                // Priority order of statuses
                const priorityOrder = {
                  Delivered: 1,
                  "Ready for Dispatch": 2,
                  Preparing: 3,
                  "On the way": 4,
                  Submitted: 5,
                  Accepted: 6,
                };

                // Get priority values (default to a high number if not in priority list)
                const priorityA = priorityOrder[a.orderStatus] || 999;
                const priorityB = priorityOrder[b.orderStatus] || 999;

                // Sort by priority
                return priorityA - priorityB;
              })
              .map((order, index) => (
                <CateringPastOrderCard
                  key={`${order.orderId}-${index}`}
                  restaurant={order.restaurant}
                  serviceItemName={order.serviceItemName}
                  date={order.date}
                  orderStatus={order.orderStatus}
                  notes={order.notes}
                  orderId={order.orderId}
                  orderNo={order.orderNo}
                  totalPrice={order.totalPrice}
                  currency={order.currency}
                  // Additional props for navigation and cart restoration
                  serviceItemId={order.serviceItemId}
                  restaurantId={order.restaurantId}
                  noOfPax={order.noOfPax}
                  serviceItemDate={order.serviceItemDate}
                  serviceItemSlot={order.serviceItemSlot}
                  serviceItems={order.serviceItems}
                  customerName={order.customerName}
                  orderDate={order.orderDate}
                  // Modification tracking
                  updatedByAgentId={order.updatedByAgentId}
                  updatedDatetime={order.updatedDatetime}
                />
              ))}
          </div>
        </div>
      ) : (
        <div className="text-gray-500 text-center py-8">
          No catering orders found.
        </div>
      )}
    </div>
  );
};

export default CateringPastOrders;
